import React, { Fragment, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { compose } from 'recompose';
import { withLocalize } from 'react-localize-redux';
import styled from 'styled-components';
import { Form, Grid } from 'semantic-ui-react';
import axios from 'axios';
import { get, has, isEmpty } from 'lodash';

import { OrginalButton } from '../../uiStandard/event/button';
import { FormSubmitButton } from '../../uiStandard/event/formInput';
import { Input } from '../../uiStandard/event/input';
import { FORGOT_PWD_RESET_SEND_CODE_EMAIL, VALIDATE_EMAIL_CODE, RESET_PWD_VIA_FORGOT_PWD } from '../network/api';
import { handleResponseCode } from '../actions';
import { HHAlert, hlog, HHEncrypt } from '../common/util';
import { ALERT_TYPE } from '../common/constant';

const RegularInput = styled(Input)`
  width: auto;
`;

const RegularButton = styled(FormSubmitButton)`
  min-height: 41.13px;
  width: 338px;
  @media (max-width: 768px) {
    width: auto;
  }
`;

const ResetPasswordV2 = props => {
  const { translate } = props;

  const [sendCodeButtonText, setSendCodeButtonText] = useState(translate('form.label.sendVerificationEmail'));
  const [sendVerificationCodeEnabled, setSendVerificationCodeEnabled] = useState(true);
  const [verificationCode, setVerificationCode] = useState('');
  const [phase, setPhase] = useState(0);
  const [email, setEmail] = useState('');
  const [tmpCode, setTmpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toEventList = (e) => {
    e.preventDefault();
    props.history.push('/');
  };

  const updateEverySecBeforeResend = (i) => {
    if (i !== 60) {
      setSendCodeButtonText(`${60 - i}${translate('seconds')}`);
    } else {
      setSendCodeButtonText(translate('form.label.sendVerificationEmail'));
      setSendVerificationCodeEnabled(true);
    }
  };

  const sendVerificationCode = async () => {
    if (isEmpty(email)) {
      HHAlert({ id: 'alert.error.fillInRequiredFields' }, ALERT_TYPE.ERROR);
      return;
    }
    try {
      const res = await axios.post(FORGOT_PWD_RESET_SEND_CODE_EMAIL, { email });
      const { data } = res;
      handleResponseCode(data.code);
      const { response } = data;
      if (get(data, 'code') === 200 && has(response, 'tmpCode') && !isEmpty(get(response, 'tmpCode'))) {
        setTmpCode(get(response, 'tmpCode'));
        HHAlert({ id: 'alert.success.verificationEmailSent' }, ALERT_TYPE.SUCCESS);
      }
    } catch (err) {
      hlog(err);
    }
  };

  const handleSendVerificationEmailClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSendVerificationCodeEnabled(false);
    await sendVerificationCode();
    for (let i = 0; i <= 60; i += 1) {
      setTimeout(() => updateEverySecBeforeResend(i), i * 1000);
    }
    setSendCodeButtonText(translate('form.label.sendVerificationEmail'));
  };

  const getBlurredUserEmail = () => {
    if (!isEmpty(email)) {
      if (!email.includes('@')) {
        return '';
      }
      const [prefix, domain] = [email.split('@')[0], email.split('@')[1]];
      if (prefix.length > 3) {
        return `${prefix.substring(0, 3)}***@${domain}`;
      } return `${prefix[0]}**@${domain}`;
    } return '';
  };

  const verifyEmailCode = async () => {
    const payload = {
      email,
      verificationCode,
      tmpCode,
      purpose: 'resetPassword'
    };
    try {
      const res = await axios.post(VALIDATE_EMAIL_CODE, payload);
      const { data } = res;
      handleResponseCode(data.code);
      if (get(data, 'code') === 200) {
        return true;
      } return false;
    } catch (err) {
      hlog(err);
      return false;
    }
  };

  const resetPassword = async () => {
    const encodedPwd = HHEncrypt(newPassword);
    const payload = {
      email,
      verificationCode,
      tmpCode,
      newPassword: encodedPwd
    };
    try {
      const res = await axios.post(RESET_PWD_VIA_FORGOT_PWD, payload);
      const { data } = res;
      handleResponseCode(data.code);
      if (get(data, 'code') === 200) {
        return true;
      } return false;
    } catch (err) {
      hlog(err);
      return false;
    }
  };

  const handleNextStepClick = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    let codeVerifyResult = false;
    let resetPwdResult = false;
    switch (phase) {
      case 0:
        if (isEmpty(email)) {
          HHAlert({ id: 'alert.error.fillInRequiredFields' }, ALERT_TYPE.ERROR);
          setIsSubmitting(false);
          return;
        }
        break;
      case 1:
        if (isEmpty(email) || isEmpty(verificationCode)) {
          HHAlert({ id: 'alert.error.fillInRequiredFields' }, ALERT_TYPE.ERROR);
          setIsSubmitting(false);
          return;
        }
        codeVerifyResult = await verifyEmailCode();
        if (!codeVerifyResult) {
          setIsSubmitting(false);
          return;
        }
        break;
      case 2:
        if (isEmpty(email) || isEmpty(verificationCode)) {
          HHAlert({ id: 'alert.error.fillInRequiredFields' }, ALERT_TYPE.ERROR);
          setIsSubmitting(false);
          return;
        }
        if (isEmpty(newPassword) || isEmpty(confirmPassword)) {
          HHAlert({ id: 'alert.error.fillInRequiredFields' }, ALERT_TYPE.ERROR);
          setIsSubmitting(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          HHAlert({ id: 'alert.error.passwordNotMathed' }, ALERT_TYPE.ERROR);
          setIsSubmitting(false);
          return;
        }
        resetPwdResult = await resetPassword();
        if (!resetPwdResult) {
          setIsSubmitting(false);
          return;
        }
        props.history.push('/login');
        return;
      default:
        return;
    }
    setIsSubmitting(false);
    setPhase(phase + 1);
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    if (phase !== 0) {
      setPhase(phase - 1);
    } else {
      props.history.push('/login');
    }
  };

  return (
    <Fragment>
      <Helmet>
        <title> {translate('headTitle.resetPasswordForm')} </title>
      </Helmet>
      <Container>
        <TopLeftLogo onClick={(e) => toEventList(e)} />
        <BackButton onClick={(e) => handleBackClick(e)}>
          <WhiteLeftArrow />{translate('back')}
        </BackButton>
        <Body>
          <CurvedBox>
            <BoxContainer>
              <Form>
                <Grid centered>
                  <Grid.Row>
                    {(phase === 0 || phase === 1) &&
                      <Title>{translate('forgot.password')}</Title>
                    }
                    {phase === 2 &&
                      <Title>{translate('forgot.password.reset')}</Title>
                    }
                  </Grid.Row>
                  {phase === 0 &&
                    <Grid.Row>
                      <Grid.Column width={16} textAlign='left'>
                        <RegularInput
                          form={{
                            setFieldValue: (fieldName, value) => {
                              setEmail(value);
                            }
                          }}
                          otherProps={{
                            label: translate('form.label.emailAddress'),
                            type: 'email',
                            placeholder: translate('form.placeholder.emailAddress')
                          }}
                          field={{
                            name: 'email'
                          }}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  }
                  {phase === 1 &&
                    <Fragment>
                      <Grid.Row>
                        <Grid.Column width={9} textAlign='left'>
                          <RegularInput
                            form={{
                              setFieldValue: (fieldName, value) => {
                                setVerificationCode(value);
                              }
                            }}
                            otherProps={{
                              placeholder: translate('form.placeholder.emailVerificationCode'),
                              type: 'text',
                              label: translate('form.label.emailVerificationCode')
                            }}
                            field={{
                              name: 'verificationCode'
                            }}
                          />
                        </Grid.Column>
                        <Grid.Column width={7} textAlign='right' verticalAlign='bottom'>
                          <SendCodeButton
                            isPrimary
                            isSmall
                            onClick={(e) => handleSendVerificationEmailClick(e)}
                            disabled={!sendVerificationCodeEnabled}
                          >
                            {sendCodeButtonText}
                          </SendCodeButton>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <VerificationDescText>
                            {`${translate('form.label.emailVerificationDescText', { email: getBlurredUserEmail() })}`}
                          </VerificationDescText>
                        </Grid.Column>
                      </Grid.Row>
                    </Fragment>
                  }
                  {phase === 2 &&
                    <Fragment>
                      <Grid.Row>
                        <Grid.Column width={16} textAlign='left'>
                          <RegularInput
                            form={{
                              setFieldValue: (fieldName, value) => {
                                setNewPassword(value);
                              }
                            }}
                            otherProps={{
                              label: translate('New Password'),
                              type: 'password',
                              placeholder: translate('form.placeholder.password')
                            }}
                            field={{
                              name: 'newPassword'
                            }}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16} textAlign='left'>
                          <RegularInput
                            form={{
                              setFieldValue: (fieldName, value) => {
                                setConfirmPassword(value);
                              }
                            }}
                            otherProps={{
                              label: translate('form.label.confirmNewPassword'),
                              type: 'password',
                              placeholder: translate('form.placeholder.reEnterPassword')
                            }}
                            field={{
                              name: 'comfirmPassword'
                            }}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Fragment>
                  }
                  <Grid.Row>
                    <Grid.Column width={16} textAlign='center'>
                      {/* <RegularButton isPrimary onClick={(e) => handleNextStepClick(e)}>
                        {(phase === 0 || phase === 1) &&
                          translate('button.next')
                        }
                        {phase === 2 && translate('forgot.password.reset')}
                      </RegularButton> */}
                      <RegularButton
                        onClick={(e) => handleNextStepClick(e)}
                        isSubmitting={isSubmitting}
                        label={(phase === 0 || phase === 1) ? translate('button.next') : translate('forgot.password.reset')}
                        type='button'
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form>
            </BoxContainer>
          </CurvedBox>
        </Body>
      </Container>
    </Fragment>
  );
};

const Container = styled.div`
  background-image: url('/img/event/event-signup-bg.png');
  background-repeat: no-repeat;
  background-size: 100% 100%;
  height: 100%;
   @media (max-width: 768px) {
    text-align: center;
  }
`;

const Body = styled.div`
  text-align: center;
`;

const CurvedBox = styled.div`
  background-color: #FFFFFF;
  border-radius: 18px;
  min-height: 100px;
  display: inline-block;
  @media (min-width: 768px) {
    width: 469px;
    margin-top: 60px;
  }
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 50px;
    margin-bottom: 25px;
  }
`;

const BoxContainer = styled.div`
  margin-top: 30px;
  margin-bottom: 30px;
  margin-left: 60px;
  margin-right: 60px;
`;

const TopLeftLogo = styled.button`
  background-image: url('/img/cms/create-event-logo.png');
  background-repeat: no-repeat;
  height: 36px;
  width: 170px;
  background-size: 100%;
  display: inline-block;
  border: none;
  background-color: transparent;
  @media (min-width: 768px) {
    top: 20px;
    left: 200px;
    position: absolute;
  }
`;

const BackButton = styled.button`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  line-height: 19px;
  color: #FFFFFF;

  height: 20px;
  width: 70px;
  font-size: 17px;
  border: none;
  background-color: transparent;
  @media (min-width: 768px) {
    top: 110px;
    left: 200px;
    position: absolute;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const WhiteLeftArrow = styled.div`
  background-image: url('/img/icons/white-left-angle.png');
  background-repeat: no-repeat;
  background-size: 100%;
  height: 13px;
  width: 8px;
  display: inline-block;
  margin-right: 10px;
`;

const Title = styled.div`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 700;
  font-size: 30px;
  line-height: 36px;
  color: #333333;
`;

const VerificationDescText = styled.div`
  font-family: 'Quicksand';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  color: #809FB8;
`;

// const RegularButton = styled(OrginalButton).attrs(() => ({
//   type: 'button'
// }))`
//   width: 338px;
//   @media (max-width: 768px) {
//     width: auto;
//   }
// `;

const SendCodeButton = styled(OrginalButton).attrs(() => ({
  type: 'button'
}))`
  width: 133px;
`;

export default compose(
  withLocalize,
  withRouter
)(ResetPasswordV2);



// WEBPACK FOOTER //
// ./src/components/login/resetPasswordV2.js